import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";

import type { UserWithAudienceDto } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { FormMultiSelect } from "@/components/Form/FormMultiSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { createRequiredStringRule } from "@/helpers/rules";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useTranslation } from "@/translations";

const FORM_ID = "inform-admin-modal-form";

interface FormValues {
  type: string;
  content: string;
  adminsToInform: UserWithAudienceDto[];
}

type InformAdminModalProps = ModalBaseProps & {
  messageId: string;
};

const MIN_LENGTH_CONTENT = 10;
const MAX_LENGTH_CONTENT = 1000;

export function InformAdminModal({ messageId, isOpened, onOpenChange }: InformAdminModalProps): React.ReactNode {
  const { t } = useTranslation();
  const communityFeedQueries = useCommunityFeedQueries();
  const form = useForm<FormValues>();
  const informAdminsMutation = communityFeedMutations.useInformAdmins(messageId);

  const { data: adminListData, isLoading } = useQuery({
    ...communityFeedQueries.adminsToInform(messageId),
    enabled: isOpened,
  });

  const adminList = useMemo(() => adminListData?.items ?? [], [adminListData]);

  async function handleShare() {
    const formValues = form.getValues();

    await informAdminsMutation.mutateAsync({
      content: formValues.content,
      adminsToInform: formValues.adminsToInform.map((admin) => admin.id),
    });
    onOpenChange(false);
  }

  return (
    <Modal.Root
      size="sm"
      title={t("component.community-post.inform-admin.modal.title")}
      description={t("component.community-post.inform-admin.modal.description")}
      onExitComplete={() => {
        form.reset();
      }}
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <section data-testid="inform-admin-modal-content">
          {isLoading ? (
            <Box padding={{ y: "md" }}>
              <Inline justify="center">
                <Spinner size="md" />
              </Inline>
            </Box>
          ) : (
            <Form.Root id={FORM_ID} formMethods={form} onSubmit={handleShare}>
              <Form.Body>
                <FormField label={t("component.community-post.inform-admin.modal.input.admins.title")} required>
                  <FormMultiSelect<FormValues, "adminsToInform">
                    name="adminsToInform"
                    placeholder={t("component.community-post.inform-admin.modal.input.admins.placeholder")}
                    disabledItemTooltip={t("component.community-post.inform-admin.modal.admin-list.disabled-tooltip")}
                    items={adminList}
                    disabledItems={adminList.filter((x) => !x.canAccess)}
                    keySelector={(x) => x.id}
                    usePortal
                    renderOption={(x, selected) => (
                      <span className="flex max-w-full flex-col text-left">
                        <span className="truncate">{x.fullName}</span>
                        {!selected && x.email ? (
                          <span className="text-caption text-text-secondary italic">({x.email})</span>
                        ) : null}
                      </span>
                    )}
                    searchableFieldSelector={(x) => [x.fullName, x.email || ""]}
                    rules={{
                      required: t("components.form.error.required", {
                        inputName: t("component.community-post.inform-admin.modal.input.admins.title"),
                      }),
                    }}
                  />
                </FormField>
                <FormField label={t("component.community-post.inform-admin.modal.input.content.title")} required>
                  <FormTextArea<FormValues>
                    data-testid="inform-admin-description"
                    name="content"
                    placeholder={t("component.community-post.inform-admin.modal.input.content.placeholder")}
                    rules={{
                      minLength: {
                        message: t("components.form.error.min-length", { length: MIN_LENGTH_CONTENT }),
                        value: MIN_LENGTH_CONTENT,
                      },
                      maxLength: {
                        message: t("components.form.error.max-length", { length: MAX_LENGTH_CONTENT }),
                        value: MAX_LENGTH_CONTENT,
                      },
                      validate: {
                        required: createRequiredStringRule(
                          t,
                          "component.community-post.inform-admin.modal.input.content.title",
                        ),
                      },
                    }}
                  />
                </FormField>
              </Form.Body>
            </Form.Root>
          )}
        </section>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" tone="primary">
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button
          data-testid="submit-inform-btn"
          form={FORM_ID}
          variant="filled"
          type="submit"
          isLoading={informAdminsMutation.isPending}
        >
          {t("component.community-post.inform-admin.modal.share-button")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
